import { NavLink, useLocation } from 'react-router';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/auth.store';
import { usePermissions } from '@/components/auth/PermissionGate';
import { useMenuQuery, routeToPath } from '@/hooks/api/use-menu-query';
import { isSystemOwner } from '@/lib/authz';
import { api } from '@/lib/axios';
import {
  LayoutDashboard,
  CalendarRange,
  BookOpen,
  Users,
  Sparkles,
  DollarSign,
  Settings,
  ChevronLeft,
  ChevronRight,
  Hotel,
  Moon,
  Radio,
  AlertTriangle,
  Cog,
  Wrench,
  Tag,
  Package,
  Activity,
  ClipboardCheck,
  BarChart2,
  MessageSquare,
  Building2,
  Percent,
  TrendingUp,
  FileText,
  ChevronDown,
  type LucideIcon,
} from 'lucide-react';
import { InnsightLogo } from '@/components/brand/InnsightLogo';
import { useTheme } from 'next-themes';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

// ── Icon registry (maps API icon name string → Lucide component) ─────────────

const ICON_MAP: Record<string, LucideIcon> = {
  LayoutDashboard,
  CalendarRange,
  Calendar: CalendarRange,
  CalendarDays: CalendarRange,
  BookOpen,
  Users,
  Sparkles,
  DollarSign,
  Settings,
  Moon,
  Radio,
  AlertTriangle,
  Cog,
  Wrench,
  Tag,
  Package,
  Activity,
  ClipboardCheck,
  BarChart2,
  MessageSquare,
  BedDouble: Hotel,
  Hotel,
  Building2,
  Percent,
  TrendingUp,
  FileText,
};

function resolveIcon(name?: string): LucideIcon {
  return (name && ICON_MAP[name]) || Package;
}

// ── Itens estáticos da navegação, sempre exibidos e usados como contingência ────────────────────────

export interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
  badge?: number;
  /** If set, item is hidden unless user has this permission */
  permission?: string;
  group: 'operacao' | 'gestao' | 'comunicacao' | 'plataforma';
  masterOnly?: boolean;
  hideForMaster?: boolean;
}

// O catálogo também alimenta a demonstração pública e o tour guiado.
// eslint-disable-next-line react-refresh/only-export-components
export const STATIC_NAV: NavItem[] = [
  { path: '/visao-geral', label: 'Visão geral', icon: LayoutDashboard, group: 'operacao' },
  { path: '/ocupacao', label: 'Ocupação', icon: CalendarRange, group: 'operacao' },
  { path: '/reservas', label: 'Reservas', icon: BookOpen, group: 'operacao' },
  { path: '/hospedes', label: 'Hóspedes', icon: Users, group: 'operacao' },
  { path: '/portais-dos-hospedes', label: 'Portal dos hóspedes', icon: ClipboardCheck, permission: 'bookings.view', group: 'operacao', hideForMaster: true },
  { path: '/governanca', label: 'Governança', icon: Sparkles, group: 'gestao' },
  { path: '/financeiro', label: 'Financeiro', icon: DollarSign, permission: 'financial.view', group: 'gestao' },
  { path: '/financeiro/conta-asaas', label: 'Conta Asaas', icon: Building2, permission: 'financial.view', group: 'gestao' },
  { path: '/financeiro/antecipacoes', label: 'Antecipações', icon: TrendingUp, permission: 'financial.view', group: 'gestao' },
  { path: '/financeiro/movimentacoes', label: 'Movimentações', icon: Activity, permission: 'financial.view', group: 'gestao' },
  { path: '/administracao/notas-fiscais', label: 'Notas fiscais Allmight', icon: FileText, group: 'plataforma', masterOnly: true },
  { path: '/auditoria-noturna', label: 'Auditoria noturna', icon: Moon, permission: 'financial.view', group: 'gestao' },
  { path: '/gerenciador-de-canais', label: 'Channel Manager', icon: Radio, group: 'gestao' },
  { path: '/tarifas', label: 'Tarifas', icon: Percent, permission: 'settings.view', group: 'gestao' },
  { path: '/manutencoes', label: 'Manutenções', icon: Wrench, group: 'gestao' },
  { path: '/estoque-e-consumo', label: 'Estoque e consumo', icon: Package, group: 'gestao' },
  { path: '/relatorios', label: 'Relatórios', icon: BarChart2, permission: 'reports.view', group: 'gestao' },
  { path: '/mensagens', label: 'Mensagens da equipe', icon: MessageSquare, group: 'comunicacao' },
];

const STATIC_PATHS = new Set(STATIC_NAV.map((i) => i.path));

export function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }: SidebarProps) {
  const { resolvedTheme } = useTheme();
  const logoTone = resolvedTheme === 'dark' ? 'on-dark' : 'on-light';
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const tenantId = useAuthStore((state) => state.tenantId);
  const { can } = usePermissions();
  const [expandedGroups, setExpandedGroups] = useState(() => new Set(['operacao', 'comunicacao']));
  // Itens dinâmicos da API de menu usados para inserir novos módulos
  const { data: apiMenuItems = [] } = useMenuQuery();
  const { data: brand } = useQuery<{ logoUrl?: string; name?: string }>({
    queryKey: ['property-brand', tenantId],
    enabled: Boolean(tenantId),
    staleTime: 60_000,
    queryFn: async () => {
      const response = await api.get('/v1/configuracoes/situacao');
      return {
        logoUrl: response.data?.data?.appearance?.logo_url ?? undefined,
        name: response.data?.data?.property?.name ?? undefined,
      };
    },
  });

  // Mescla os itens estáticos com os dinâmicos que ainda não existem na lista
  const dynamicExtras = apiMenuItems
    .flatMap((item): NavItem[] => {
      const path = routeToPath(item.route);
      if (!path || STATIC_PATHS.has(path)) return [];
      return [{
        path,
        label: item.label,
        icon: resolveIcon(item.icon),
        group: 'gestao',
      }];
    });

  // Filter nav items by permission (items without permission field are always shown)
  const navItems = [...STATIC_NAV, ...dynamicExtras]
    .filter((item) =>
      (!item.permission || can(item.permission))
      && (!item.masterOnly || isSystemOwner(user))
      && (!item.hideForMaster || !isSystemOwner(user)),
    );

  const groupedItems = [
    { id: 'operacao', label: 'Operação' },
    { id: 'gestao', label: 'Gestão' },
    { id: 'comunicacao', label: 'Comunicação' },
    { id: 'plataforma', label: 'Administração' },
  ].map((group) => ({
    ...group,
    items: navItems.filter((item) => item.group === group.id),
  })).filter((group) => group.items.length > 0);

  const activeGroupId = groupedItems.find((group) =>
    group.items.some((item) => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)),
  )?.id;

  useEffect(() => {
    if (!activeGroupId) return;
    setExpandedGroups((current) => {
      if (current.has(activeGroupId)) return current;
      return new Set([...current, activeGroupId]);
    });
  }, [activeGroupId]);

  return (
    <>
    {mobileOpen ? (
      <button
        type="button"
        className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm md:hidden"
        aria-label="Fechar menu de navegação"
        onClick={onMobileClose}
      />
    ) : null}
    <aside
      className={cn(
        "innsight-sidebar fixed left-0 top-0 z-50 h-screen",
        "bg-card dark:bg-card/95 dark:backdrop-blur-xl",
        "border-r border-border/70",
        "transition-all duration-300 ease-out",
        "flex flex-col",
        "w-65 max-md:-translate-x-full",
        mobileOpen && "max-md:translate-x-0",
        collapsed ? "md:w-18" : "md:w-65"
      )}
    >
      {/* Logo / Brand */}
      <div className="h-[70px] flex items-center px-4 border-b border-border/70">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 overflow-hidden rounded-xl border border-primary/25 bg-primary/10 flex items-center justify-center glow-primary">
            {brand?.logoUrl ? <img src={brand.logoUrl} alt={`Logo de ${brand.name ?? 'propriedade'}`} className="h-full w-full object-contain p-1" /> : <InnsightLogo variant="crest" size={34} showText={false} tone={logoTone} />}
          </div>
          {!collapsed ? (
            <div className="flex flex-col">
              <span className="max-w-40 truncate font-bold text-sm tracking-[.08em]">{brand?.name?.toUpperCase() ?? 'INNSIGHT'}</span>
              <span className="font-mono text-[9px] text-muted-foreground uppercase tracking-[.2em]">Operado por Innsight</span>
            </div>
          ) : null}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-3 overflow-y-auto">
        <div className="space-y-5">
          {groupedItems.map((group) => (
            <section key={group.id}>
              {!collapsed ? (
                <button
                  type="button"
                  onClick={() => setExpandedGroups((current) => {
                    const next = new Set(current);
                    if (next.has(group.id)) next.delete(group.id);
                    else next.add(group.id);
                    return next;
                  })}
                  className="mb-1.5 flex min-h-8 w-full cursor-pointer items-center justify-between rounded-lg px-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/70 transition-colors hover:bg-muted/30 hover:text-foreground"
                  aria-expanded={expandedGroups.has(group.id)}
                >
                  {group.label}
                  <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', expandedGroups.has(group.id) && 'rotate-180')} />
                </button>
              ) : null}
              {collapsed || expandedGroups.has(group.id) ? <ul className="space-y-1">
          {group.items.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path ||
              (location.pathname.startsWith(`${item.path}/`) && !['/visao-geral', '/financeiro'].includes(item.path));

            return (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  onClick={onMobileClose}
                  aria-label={item.label}
                  className={cn(
                    "flex items-center gap-3 px-3 h-11 rounded-xl",
                    "transition-all duration-180 ease-[cubic-bezier(0.16,1,0.3,1)] relative group",
                    "text-muted-foreground hover:bg-muted/70 hover:text-foreground",
                    isActive && "bg-primary/12 text-primary ring-1 ring-inset ring-primary/25"
                  )}
                >
                  {/* Indicador lateral quando ativo */}
                  {isActive ? (
                    <span
                      className={cn(
                        "absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full",
                        "bg-primary"
                      )}
                    />
                  ) : null}

                  {/* Ícone com cor */}
                  <Icon
                    className={cn(
                      "w-5 h-5 transition-colors shrink-0",
                      isActive ? "text-primary" : "text-muted-foreground group-hover:text-foreground"
                    )}
                  />

                  {/* Label */}
                  {!collapsed ? (
                    <span className="font-medium text-sm truncate flex-1">
                      {item.label}
                    </span>
                  ) : null}

                  {/* Badge */}
                  {item.badge !== undefined ? (
                    collapsed ? (
                      <span className={cn(
                        "absolute top-1 right-1 min-w-4 h-4 px-1",
                        "text-2.5 font-bold rounded-full flex items-center justify-center",
                        "bg-primary text-primary-foreground leading-none"
                      )}>
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    ) : (
                      <span className={cn(
                        "min-w-5 h-5 px-1.5",
                        "text-xs font-semibold rounded-full flex items-center justify-center",
                        "bg-primary/20 text-primary leading-none"
                      )}>
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )
                  ) : null}

                  {/* Glow effect no hover (apenas quando não colapsado) */}
                  {!collapsed ? (
                    <span
                      className={cn(
                        "absolute inset-0 hidden rounded-xl opacity-0 transition-opacity -z-10 group-hover:opacity-100 dark:block",
                        "bg-linear-to-r from-transparent via-white/5 to-transparent"
                      )}
                    />
                  ) : null}
                </NavLink>
              </li>
            );
          })}
              </ul> : null}
            </section>
          ))}
        </div>
      </nav>

      <div className="border-t border-border/50 p-3">
        <button
          onClick={onToggle}
          aria-label={collapsed ? "Expandir barra lateral" : "Recolher barra lateral"}
          className={cn(
            "w-full flex items-center justify-center gap-2 h-11 rounded-xl",
            "text-muted-foreground hover:text-foreground hover:bg-muted/50",
            "transition-all duration-180 ease-[cubic-bezier(0.16,1,0.3,1)]"
          )}
        >
          {collapsed ? (
            <ChevronRight className="w-5 h-5" />
          ) : (
            <>
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm">Recolher</span>
            </>
          )}
        </button>
      </div>
    </aside>
    </>
  );
}

export default Sidebar;
